import { useEffect, useState } from 'react';
import axios from 'axios';
import { useEditor } from "../../../../../../App.jsx";
import { IconTrash, IconUsers, IconEdit } from "../../../../../Customiz3r/components/ToolSelectorTop/Icons.jsx";
import { confirmDelete, showAlert } from "/src/components/HelperAlerts/HelpersAlerts.jsx";
import { getThemeContext } from "../../../../../../components/providers/ThemeProvider.jsx";
import {
    MapPin,
    Phone,
    Mail,
    User,
    Calendar,
    ExternalLink,
    FileText,
    Users as UsersIcon,
    Tag
} from 'lucide-react';
import AttachmentsList from "./components/AttachmentsList.jsx";
import getMediaUrl from "../../../../../../components/tools/utils.jsx";

const ClienteViewer = () => {
    const [cliente, setCliente] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [users, setUsers] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const { stateEditor, setStateEditor } = useEditor();
    const theme = getThemeContext();

    // Cargar datos del cliente
    useEffect(() => {
        if (!stateEditor?.editorID) return;

        const loadData = async () => {
            try {
                setLoading(true);

                // Cargar usuarios para mostrar el nombre del usuario asociado
                const usersResponse = await axios.get('/list_users/');
                setUsers(usersResponse.data.results || usersResponse.data);

                // Cargar datos del cliente
                const clienteResponse = await axios.get(`/clients/${stateEditor.editorID}/`);
                setCliente(clienteResponse.data);

                // Cargar adjuntos
                const attachmentsResponse = await axios.get(`/clients/${stateEditor.editorID}/attachments/`);
                const attachmentsData = Array.isArray(attachmentsResponse.data)
                    ? attachmentsResponse.data
                    : attachmentsResponse.data.results || [];
                setAttachments(attachmentsData);

            } catch (err) {
                setError('No se pudieron cargar los datos del cliente.');
                console.error('Error loading client data:', err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [stateEditor.editorID]);

    // Borrar adjunto
    const handleDeleteAttachment = (id) => {
        axios.delete(`/clients/${stateEditor.editorID}/attachments/${id}/`)
            .then(() => {
                setAttachments(prev => prev.filter(a => a.id !== id));
                showAlert({ title: 'Adjunto eliminado', icon: 'success', theme });
            })
            .catch(() => {
                showAlert({ title: 'Error', text: 'No se pudo borrar el adjunto.', icon: 'error', theme });
            });
    };

    // Eliminar cliente
    const onDelete = async () => {
        if (!stateEditor.editorID) return;

        const confirmed = await confirmDelete({
            title: 'Borrar cliente',
            text: 'Esta acción no se puede revertir',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            theme
        });

        if (!confirmed) return;

        axios.delete(`/clients/${stateEditor.editorID}/`)
            .then(() => {
                showAlert({ title: 'Cliente borrado', text: 'El cliente se ha borrado correctamente.', icon: 'success', theme });
                setStateEditor({ editor: false, editorID: null, editorType: null });
            })
            .catch(() => {
                showAlert({ title: 'Error', text: 'No se pudo borrar el cliente.', icon: 'error', theme });
            });
    };

    // Obtener nombre del usuario asociado
    const getUserName = (userId) => {
        if (!userId) return 'Sin asignar';
        const user = users.find(u => u.id === userId);
        return user ? (user.public_username || user.email) : 'Usuario no encontrado';
    };

    // Obtener badge de estado
    const getStatusBadge = (status) => {
        const statusConfig = {
            'LEAD': { color: 'bg-yellow-100 text-yellow-800', text: 'Lead' },
            'ACTIVE': { color: 'bg-green-100 text-green-800', text: 'Activo' },
            'INACTIVE': { color: 'bg-red-100 text-red-800', text: 'Inactivo' }
        };

        const config = statusConfig[status] || { color: 'bg-gray-100 text-neutral-400', text: status };

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                <Tag className="w-3 h-3 mr-1" />
                {config.text}
            </span>
        );
    };

    // Formatear fecha
    const formatDate = (dateString) => {
        if (!dateString) return 'No disponible';
        try {
            return new Date(dateString).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Fecha inválida';
        }
    };

    const prospectLogos = cliente?.prospect_logos || [];

    if (loading) {
        return (
            <div className="flex flex-col h-full glassCard p-4">
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-gray-600">Cargando información del cliente...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !cliente) {
        return (
            <div className="flex flex-col h-full glassCard p-4">
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <p className="text-red-600 text-lg mb-4">{error || 'Cliente no encontrado'}</p>
                        <button
                            onClick={() => setStateEditor({ editor: false, editorID: null, editorType: null })}
                            className="btn btn-primary"
                        >
                            <IconUsers className="w-5 h-5 mr-2" />
                            Volver a la lista
                        </button>
                    </div>
                </div>
            </div>
        );
    }


    const handleEdit = () => {
        if (!cliente || !stateEditor.editorID) return;

        setStateEditor({
            editor: true,
            editorID: stateEditor.editorID,
            editorType: 'cliente_editor',
            // editorData: cliente
        });
    }

    return (
        <div className="flex flex-col h-full glassCard p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setStateEditor({ editor: false, editorID: null, editorType: null })}
                        className="btn"
                    >
                        <IconUsers className="w-5 h-5" />
                        <span className="ml-1">Volver</span>
                    </button>
                    <button
                        onClick={handleEdit}
                        className="btn "
                    >
                        <IconEdit className="w-5 h-5" />
                        <span className="ml-1">Editar</span>
                    </button>
                </div>

                <div className="text-center">
                    <h2 className="text-3xl font-semibold text-neutral-400">
                        {cliente.nombre} {cliente.apellido}
                    </h2>
                    <div className="mt-2">
                        {getStatusBadge(cliente.status)}
                    </div>
                </div>

                <button
                    onClick={onDelete}
                    className="btn bg-red-600 hover:bg-red-800 text-white"
                >
                    <IconTrash className="w-5 h-5" />
                    <span className="ml-1">Eliminar</span>
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto px-2">
                <div className="max-w-4xl mx-auto space-y-8">

                    {/* Información Personal */}
                    <div className="bg-white/50 dark:bg-base-200 rounded-lg p-6 shadow-sm">
                        <h3 className="text-xl font-semibold text-neutral-400 mb-4 flex items-center">
                            <User className="w-5 h-5 mr-2" />
                            Información Personal
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Nombre Completo</label>
                                <p className="text-lg text-neutral-400">{cliente.nombre} {cliente.apellido || ''}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Estado</label>
                                <div>{getStatusBadge(cliente.status)}</div>
                            </div>
                        </div>
                    </div>

                    {/* Información de Contacto */}
                    <div className="bg-white/50 dark:bg-base-200 rounded-lg p-6 shadow-sm">
                        <h3 className="text-xl font-semibold text-neutral-400 mb-4 flex items-center">
                            <Mail className="w-5 h-5 mr-2" />
                            Información de Contacto
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                                <div className="flex items-center text-neutral-400">
                                    <Mail className="w-4 h-4 mr-2 text-neutral-300" />
                                    <a href={`mailto:${cliente.email}`} className="hover:text-blue-600 transition-colors">
                                        {cliente.email}
                                    </a>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Dirección</label>
                                <div className="flex items-center text-neutral-400">
                                    <MapPin className="w-4 h-4 mr-2 text-neutral-300" />
                                    <span>{cliente.direccion || 'No especificada'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Teléfonos */}
                    {cliente.telefonos && cliente.telefonos.length > 0 && (
                        <div className="bg-white/50 dark:bg-base-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold text-neutral-400 mb-4 flex items-center">
                                <Phone className="w-5 h-5 mr-2" />
                                Teléfonos
                            </h3>
                            <div className="space-y-2">
                                {cliente.telefonos.map((telefono, idx) => (
                                    <div key={idx} className="flex items-center text-neutral-400">
                                        <Phone className="w-4 h-4 mr-2 text-neutral-300" />
                                        <a href={`tel:${telefono.numero}`} className="hover:text-blue-600 transition-colors">
                                            {telefono.numero}
                                        </a>
                                        {telefono.tipo && (
                                            <span className="ml-2 text-sm text-neutral-300">({telefono.tipo})</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Gestión */}
                    <div className="bg-white/50 dark:bg-base-200 rounded-lg p-6 shadow-sm">
                        <h3 className="text-xl font-semibold text-neutral-400 mb-4 flex items-center">
                            <UsersIcon className="w-5 h-5 mr-2" />
                            Gestión
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Usuario Asociado</label>
                                <p className="text-neutral-400">{getUserName(cliente.user)}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Fecha de Creación</label>
                                <div className="flex items-center text-neutral-400">
                                    <Calendar className="w-4 h-4 mr-2 text-neutral-300" />
                                    <span>{formatDate(cliente.created_at)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Redes Sociales */}
                    {cliente.redes_sociales && cliente.redes_sociales.length > 0 && (
                        <div className="bg-white/50 dark:bg-base-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold text-neutral-400 mb-4 flex items-center">
                                <ExternalLink className="w-5 h-5 mr-2" />
                                Redes Sociales
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {cliente.redes_sociales.map((red, idx) => (
                                    <div key={idx} className="flex items-center">
                                        <ExternalLink className="w-4 h-4 mr-2 text-neutral-300" />
                                        <span className="font-medium text-gray-600 mr-2">
                                            {red.plataforma === 'OTHER' ? 'Otra' : red.plataforma}:
                                        </span>
                                        <a
                                            href={red.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 transition-colors truncate"
                                        >
                                            {red.url}
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Material recopilado durante la prospección */}
                    {prospectLogos.length > 0 && (
                        <div className="bg-white/50 dark:bg-base-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold text-neutral-400 mb-2 flex items-center">
                                <FileText className="w-5 h-5 mr-2" />
                                Imágenes de prospección ({prospectLogos.length})
                            </h3>
                            <p className="text-sm text-base-content/60 mb-4">
                                Material obtenido por el agente antes o durante el alta del cliente.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {prospectLogos.map((logo) => {
                                    const fileUrl = getMediaUrl(logo.file);
                                    const filename = logo.logo_original_name || logo.file?.split('/').pop() || 'Logo';

                                    return (
                                        <article
                                            key={logo.lead_id}
                                            className="overflow-hidden rounded-xl border border-base-300 bg-base-100"
                                        >
                                            <a
                                                href={fileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block bg-white p-4"
                                                title="Abrir imagen a tamaño completo"
                                            >
                                                <img
                                                    src={fileUrl}
                                                    alt={`Logo de ${logo.company_name || cliente.nombre}`}
                                                    className="h-48 w-full object-contain"
                                                />
                                            </a>
                                            <div className="p-4 space-y-2">
                                                <p className="font-medium text-base-content break-all">{filename}</p>
                                                <div className="flex flex-wrap gap-2 text-xs text-base-content/60">
                                                    {logo.logo_width && logo.logo_height && (
                                                        <span>{logo.logo_width} × {logo.logo_height} px</span>
                                                    )}
                                                    {logo.logo_mime_type && <span>{logo.logo_mime_type}</span>}
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    <a
                                                        href={fileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-primary"
                                                    >
                                                        <ExternalLink className="w-4 h-4" />
                                                        Abrir imagen
                                                    </a>
                                                    {logo.logo_source_url && (
                                                        <a
                                                            href={logo.logo_source_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-ghost"
                                                        >
                                                            Ver fuente
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Adjuntos */}
                    {attachments.length > 0 ? (
                        <div className="bg-white/50 dark:bg-base-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold text-neutral-400 mb-4 flex items-center">
                                <FileText className="w-5 h-5 mr-2" />
                                Adjuntos ({attachments.length})
                            </h3>
                            <AttachmentsList
                                attachments={attachments}
                                handleDeleteAttachment={handleDeleteAttachment}
                            />
                        </div>
                        ) : (
                        <div className="bg-white/50 dark:bg-base-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold text-neutral-400 mb-4 flex items-center">
                            <FileText className="w-5 h-5 mr-2" />
                            Adjuntos ({attachments.length})
                            </h3>
                            <p className="text-neutral-400">No hay adjuntos disponibles.</p>
                        </div>
                    )}

                    {/* Información adicional */}
                    <div className="bg-white/50 dark:bg-base-200 rounded-lg p-6 shadow-sm">
                        <h3 className="text-xl font-semibold text-neutral-400 mb-4 flex items-center">
                            <Calendar className="w-5 h-5 mr-2" />
                            Información del Sistema
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">ID del Cliente</label>
                                <p className="text-neutral-400 font-mono text-sm">{cliente.id}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Última Actualización</label>
                                <p className="text-neutral-400">{formatDate(cliente.updated_at)}</p>
                            </div>
                        </div>
                    </div>


                    {/* Información del Usuario Asociado */}
                    <div className="bg-white/50 dark:bg-base-200 rounded-lg p-6 shadow-sm">
                        <h3 className="text-xl font-semibold text-neutral-400 mb-4 flex items-center">
                            <User className="w-5 h-5 mr-2" />
                            Información del Usuario Asociado
                        </h3>
                        <div className="flex items-center space-x-4">
                            {cliente.user_info?.foto_perfil && (
                                <img
                                    src={cliente.user_info.foto_perfil}
                                    alt={cliente.user_info.name}
                                    className="w-16 h-16 rounded-full object-cover"
                                />
                            )}
                            <div>
                                <p className="text-neutral-400 font-medium">{cliente.user_info?.name}</p>
                                <a
                                    href={`mailto:${cliente.user_info?.email}`}
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    {cliente.user_info?.email}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClienteViewer;
